/**
 * Router Context Types
 *
 * Defines the context passed to TanStack Router for use in beforeLoad hooks.
 * Allows route guards to access auth state without using React hooks.
 *
 * @example
 * // In route beforeLoad
 * beforeLoad: ({ context }) => {
 *   const { auth } = context;
 *   if (!auth.isAuthenticated) {
 *     throw redirect({ to: '/login' });
 *   }
 * }
 *
 * @see https://tanstack.com/router/latest/docs/framework/react/guide/router-context
 * @added DOC-0074 (2025-12-19)
 * @updated DOC-0101 (2025-12-26) - Defined minimal auth interface for FSD compliance
 */

import type { AppAbility, AppAbilityRule } from '@shared/types/ability';

/**
 * Minimal auth state interface for router context
 *
 * Defines the subset of auth state needed by route guards.
 * The actual SessionContextValue in entities/session extends this shape.
 *
 * NOTE: This interface must stay compatible with SessionContextValue
 * from @entities/session but is defined here to avoid FSD layer violations.
 */
export interface RouterAuthState {
  /** Currently authenticated user, or null if not authenticated */
  user: { id: string; email: string; systemRole: string; businessRoles?: string[] } | null;
  /** Whether a user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is being loaded (initial fetch) */
  isLoading: boolean;
  /** Session rules used by route guards before the ability context has refreshed. */
  permissions?: AppAbilityRule[] | null;
}

/**
 * Router context available in beforeLoad hooks
 *
 * Contains authentication state for route guards.
 * Extended in future for other context needs (e.g., feature flags).
 */
export interface RouterContext {
  /** Authentication state from AuthProvider */
  auth: RouterAuthState;
  /** Authorization ability from AbilityProvider */
  ability: AppAbility;
}

/**
 * Create router context from auth state
 *
 * Helper function to construct router context object.
 * Call this with auth from useAuth() when creating RouterProvider.
 *
 * @param auth - Auth context value from useAuth() (compatible with RouterAuthState)
 * @returns Router context object for RouterProvider
 */
export function createRouterContext(auth: RouterAuthState, ability: AppAbility): RouterContext {
  return { auth, ability };
}
